import { defineComponent as _defineComponent } from 'vue'
import X from '@/components/svg-components/X.vue';
import BCXTextArea from '@/components/molecules/forms/BCXTextArea.vue';
import {
  computed, PropType, ref, toRef, watch
} from 'vue';
import MyProjectsContractFileTable from '@/views/my-projects/components/contract/contractDocuments/MyProjectsContractFileTable.vue';
import UploadableItem from '@/components/molecules/UploadableItem.vue';
import BCXCheckbox from '@/components/molecules/forms/BCXCheckbox.vue';
import EditIcon from '@/components/molecules/EditIcon.vue';
import useResponsiveness from '@/mixins/useResponsiveness';
import BCXHelpTextTriggerIcon from '@/components/molecules/BCXHelpTextTriggerIcon.vue';
import BCXInfoCollapsible from '@/components/molecules/BCXInfoCollapsible.vue';
import { Contract, ContractDocument, ContractDocumentFile } from '@/models/Contracts';
import {
  createContractDocumentFile, deleteContractDocumentFile, deleteContractDocumentSignature, getContractDocumentFile, signContractDocument,
  updateContractDocument
} from '@/services/ContractService';
import useUser from '@/mixins/useUser';
import { until, watchDebounced, whenever } from '@vueuse/core';
import downloadFile from '@/utils/downloadFile';
import useContract from '@/views/my-projects/composables/useContract';
import BCXEditedIndicator from '@/components/molecules/forms/BCXEditedIndicator.vue';
import { BcxFormField } from '@/models/BcxForm';
import BCXForm from '@/components/molecules/forms/BCXForm.vue';
import useI18n from '@/mixins/useI18n';
import usePageState from '@/state/pageState';
import usePopupState from '@/state/usePopupState';


export default /*#__PURE__*/_defineComponent({
  __name: 'MyProjectsContractDetails',
  props: {
  contractId: {
    type: String as PropType<Contract['contractId']>,
    required: true,
  },
  contractDocument: {
    type: Object as PropType<ContractDocument>,
    required: true,
  },
},
  setup(__props) {

const props = __props;



const { t } = useI18n();
const { globalErrorMessage } = usePageState();

const {
  contractDetail, isUserClient, isContractStatusOpen, detectChanges, handleBackendError
} = useContract();
const { setPdf, showPopup } = usePopupState();
const contractDocument = toRef(props, 'contractDocument');

const documentSaved = ref(0);

const { userId } = useUser('self');

const isEditingDocumentTitle = ref(false);
const toggleEditDocumentTitle = () => {
  isEditingDocumentTitle.value = !isEditingDocumentTitle.value;
};
// The edit button for the title may only be displayed if the contract has the status OPEN and if the user is also the creator of the document.
const canTitleEditButtonBeDisplayed = computed(() => isContractStatusOpen && contractDocument.value?.creator?.userId === userId.value);
const comment = ref(props.contractDocument.remarks);
const titleFieldData = ref({ title: props.contractDocument.title });
const titleFormField = ref<BcxFormField[]>([
  {
    type: 'text',
    label: t('contracts.contract.contractDocuments.details.title').toString(),
    name: 'title',
    width: '100%',
    textOptions: {
      maxLength: 50
    }
  },
]);
const sameDocumentInContactDetail = computed(() => contractDetail.value?.documents.find(
  (document) => document.id === props.contractDocument.id
));
const saveTitle = async () => {
  if (!contractDetail.value?.contractId) return;

  try {
    await updateContractDocument(contractDetail.value.contractId, props.contractDocument.id, { title: titleFieldData.value.title, remarks: comment.value });
  } catch (error: any) {
    handleBackendError(error.response, 'MyProjectsContractDetails.vue');
    return;
  }

  if (sameDocumentInContactDetail.value) {
    sameDocumentInContactDetail.value.title = titleFieldData.value.title;
    documentSaved.value++;
    toggleEditDocumentTitle();
  }
};

const isCommmentsEnlarged = ref(false);

watchDebounced(comment, async () => {
  if (!contractDetail.value?.contractId) return;
  try {
    await updateContractDocument(contractDetail.value?.contractId, props.contractDocument.id, { title: titleFieldData.value.title, remarks: comment.value });
    if (sameDocumentInContactDetail.value) {
      sameDocumentInContactDetail.value.remarks = comment.value;
    }
    documentSaved.value++;
  } catch (error: any) {
    handleBackendError(error.response, 'MyProjectsContractDetails.vue');
  }
}, { debounce: 500 });

async function sendDocumentSignature(signed: boolean) {
  if (!contractDetail.value?.contractId) return;
  if (signed) {
    await signContractDocument(contractDetail.value?.contractId, props.contractDocument.id);
  } else {
    await deleteContractDocumentSignature(contractDetail.value?.contractId, props.contractDocument.id);
  }
  await detectChanges();
}

const documentSigned = computed({
  get: () => (isUserClient.value ? props.contractDocument.signedByClient : props.contractDocument.signedByContractor),
  set: async (signed: boolean) => {
    if (!contractDetail.value?.documents) return;
    if (sameDocumentInContactDetail.value) {
      if (isUserClient.value) {
        sameDocumentInContactDetail.value.signedByClient = signed;
      } else {
        sameDocumentInContactDetail.value.signedByContractor = signed;
      }
      await sendDocumentSignature(signed);
    }
  },
});

const { isMobileWidth } = useResponsiveness();

const isHelpOpened = ref(false);

const mimeTypes = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument',
  'application/vnd.oasis.opendocument.text',
  'application/rtf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/bmp',
  'image/webp',
];

const contractDocumentFiles = computed(() => contractDocument.value.files ?? []);

const deleteContractFile = async (fileId: string) => {
  if (!contractDetail.value?.contractId) return;
  try {
    await deleteContractDocumentFile(contractDetail.value?.contractId, props.contractDocument.id, fileId);
  } catch (error: any) {
    console.error(error);
    globalErrorMessage.value = { i18Key: 'contracts.contract.contractDocuments.document.files.error-deleting', code: error.code, component: 'MyProjectsContractDetails' };
    return;
  }
  const contractDocument = contractDetail.value.documents.find((document) => document.id === props.contractDocument.id);
  if (!contractDocument) return;
  contractDocument.files = contractDocument.files.filter((file) => file.id !== fileId);
  if (contractDocument.files.length === 0) {
    documentSigned.value = false;
  }
};

async function getFile(fileId: string): ContractDocumentFile | null {
  let file: ContractDocumentFile;
  try {
    file = await getContractDocumentFile(props.contractId, props.contractDocument.id, fileId);
  } catch (error: any) {
    handleBackendError(error.response, 'MyProjectsContractDetails');
    return null;
  }

  if (!file || !file.externalFile) {
    // TODO: Display error if this happens?
  }

  return file;
}

const downloadContractFile = async (fileId: string) => {
  const file: ContractDocumentFile = await getFile(fileId);
  if (file) downloadFile(file.externalFile.attachment, file.externalFile.filename, file.externalFile.fileType);
};

const viewFile = async (fileId: string) => {
  showPopup();
  const file: ContractDocumentFile = await getFile(fileId);
  if (file) setPdf(file.externalFile?.attachment, file.externalFile.filename);
};

const isSignatureEnabled = computed(() => {
  const userIdsDocumentFiles = contractDocumentFiles.value.map((file) => file.creator?.userId ?? file.creator?.company?.rootUserId);
  return userIdsDocumentFiles.includes(userId.value) && isContractStatusOpen.value;
});

const isUploadingFile = ref(false);
const fileContents = ref('');
const fileName = ref('');
const fileType = ref('');

whenever(
  () => fileContents.value.length > 0,
  async () => {
    if (!contractDetail.value?.contractId) return;

    // Make sure the fileName has updated as well before proceeding
    await until(fileName.value).not.toBe('');

    isUploadingFile.value = true;

    try {
      const contractFile = await createContractDocumentFile(contractDetail.value?.contractId, props.contractDocument.id, fileContents.value, fileName.value);
      contractDetail.value.documents = contractDetail.value.documents.map((document) => {
        if (!document.files) {
          document.files = [];
        }
        if (document.id === props.contractDocument.id) {
          document.files.push({
            ...contractFile,
            externalFile: contractFile.externalFile ? { ...contractFile.externalFile, attachment: undefined } : undefined
          });
        }
        documentSaved.value++;
        return document;
      });
    } catch (error: any) {
      handleBackendError(error.response, 'MyProjectsContractDetails.vue');
    } finally {
      isUploadingFile.value = false;
      fileContents.value = '';
      fileName.value = '';
      fileType.value = '';
    }
  }
);

watch(
  () => props.contractDocument.remarks,
  (newComment) => {
    if (newComment) comment.value = newComment;
  }
);


return { __sfc: true,props, t, globalErrorMessage, contractDetail, isUserClient, isContractStatusOpen, detectChanges, handleBackendError, setPdf, showPopup, contractDocument, documentSaved, userId, isEditingDocumentTitle, toggleEditDocumentTitle, canTitleEditButtonBeDisplayed, comment, titleFieldData, titleFormField, sameDocumentInContactDetail, saveTitle, isCommmentsEnlarged, sendDocumentSignature, documentSigned, isMobileWidth, isHelpOpened, mimeTypes, contractDocumentFiles, deleteContractFile, getFile, downloadContractFile, viewFile, isSignatureEnabled, isUploadingFile, fileContents, fileName, fileType, X, BCXTextArea, MyProjectsContractFileTable, UploadableItem, BCXCheckbox, EditIcon, BCXHelpTextTriggerIcon, BCXInfoCollapsible, BCXEditedIndicator, BCXForm }
}

})